import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Save, Eye, EyeOff, ImagePlus, MoveVertical } from "lucide-react";
import RichTextEditor from "@/components/admin/RichTextEditor";
import MediaPicker from "@/pages/admin/MediaPicker";

const SECTIONS = [
  { value: "clergy", label: "Clergy (Rabbi, Cantor)" },
  { value: "office", label: "Office / Administration" },
  { value: "religious_school", label: "Religious School" },
  { value: "youth", label: "Youth Group Leaders" },
  { value: "board", label: "Board of Directors" },
  { value: "other", label: "Other" },
];

const empty = {
  name: "",
  role: "",
  section: "office",
  bio: "",
  photo_url: "",
  email: "",
  phone: "",
  hours: "",
  is_visible: true,
  display_order: 100,
};

export default function AdminStaff() {
  const { authedClient } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedClient().get("/admin/staff");
      setStaff(data);
    } catch {
      toast.error("Could not load staff");
    } finally {
      setLoading(false);
    }
  }, [authedClient]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setForm(empty); setEditing("new"); };
  const startEdit = (s) => { setForm({ ...empty, ...s }); setEditing(s.id); };
  const cancelEdit = () => { setEditing(null); setForm(empty); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Name is required");
    try {
      if (editing === "new") {
        const { data } = await authedClient().post("/admin/staff", form);
        setStaff((p) => [...p, data]);
        toast.success("Staff member added");
      } else {
        const { data } = await authedClient().patch(`/admin/staff/${editing}`, form);
        setStaff((p) => p.map((s) => (s.id === editing ? data : s)));
        toast.success("Saved");
      }
      cancelEdit();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const toggleVisible = async (s) => {
    try {
      const { data } = await authedClient().patch(`/admin/staff/${s.id}`, { is_visible: !s.is_visible });
      setStaff((p) => p.map((x) => (x.id === s.id ? data : x)));
    } catch { toast.error("Could not toggle"); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.name}?`)) return;
    try {
      await authedClient().delete(`/admin/staff/${s.id}`);
      setStaff((p) => p.filter((x) => x.id !== s.id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  // group by section for display
  const grouped = SECTIONS.map(sec => ({
    ...sec,
    items: staff.filter(s => s.section === sec.value).sort((a, b) => a.display_order - b.display_order),
  })).filter(g => g.items.length > 0 || editing);

  return (
    <div data-testid="admin-staff-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="eyebrow mb-2">★ CMS</p>
          <h1 className="font-display text-3xl text-[color:var(--blue-ink)]">Staff & Board Members</h1>
          <p className="text-sm text-[color:var(--ink-soft)] mt-1">
            Manage everyone shown on the About Us pages. Set section, order, photo, and bio.
          </p>
        </div>
        {!editing && (
          <button onClick={startNew} className="btn-primary" data-testid="admin-staff-new">
            <Plus className="w-4 h-4" /> Add person
          </button>
        )}
      </div>

      {editing && (
        <form onSubmit={save} className="bg-[color:var(--cream)] rounded-2xl p-6 mb-8 space-y-4" data-testid="admin-staff-form">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl text-[color:var(--blue-ink)]">
              {editing === "new" ? "Add a person" : "Edit person"}
            </h2>
            <button type="button" onClick={cancelEdit}><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name *">
              <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className={ic} data-testid="staff-form-name" />
            </Field>
            <Field label="Role / Title">
              <input value={form.role} onChange={e => setForm({...form, role: e.target.value})} className={ic} placeholder="e.g. Religious School Director" />
            </Field>
            <Field label="Section">
              <select value={form.section} onChange={e => setForm({...form, section: e.target.value})} className={ic} data-testid="staff-form-section">
                {SECTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Display order (smaller shows first)">
              <input type="number" value={form.display_order} onChange={e => setForm({...form, display_order: parseInt(e.target.value || "0", 10)})} className={ic} />
            </Field>
            <Field label="Email">
              <input type="email" value={form.email || ""} onChange={e => setForm({...form, email: e.target.value})} className={ic} />
            </Field>
            <Field label="Phone">
              <input value={form.phone || ""} onChange={e => setForm({...form, phone: e.target.value})} className={ic} />
            </Field>
            <Field label="Hours (office staff only)">
              <input value={form.hours || ""} onChange={e => setForm({...form, hours: e.target.value})} className={ic} placeholder="Tue - Fri, 9 AM - 2 PM" />
            </Field>
          </div>

          <Field label="Photo">
            <div className="flex items-center gap-3">
              {form.photo_url ? (
                <img src={form.photo_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${form.photo_url}` : form.photo_url}
                     alt="" className="w-20 h-20 object-cover rounded-lg border border-[color:var(--line)]" />
              ) : (
                <div className="w-20 h-20 rounded-lg border border-dashed border-[color:var(--line)] flex items-center justify-center text-[color:var(--muted)]">
                  <ImagePlus className="w-5 h-5" />
                </div>
              )}
              <div className="flex-1 space-y-2">
                <input value={form.photo_url || ""} onChange={e => setForm({...form, photo_url: e.target.value})} className={ic} placeholder="/photo.jpg or full URL" />
                <div className="flex gap-2">
                  <button type="button" onClick={() => setPickerOpen(true)} className="btn-ghost text-sm py-2 px-3">Pick from library</button>
                  {form.photo_url && <button type="button" onClick={() => setForm({...form, photo_url: ""})} className="text-sm text-red-600 hover:underline">Remove</button>}
                </div>
              </div>
            </div>
          </Field>

          <Field label="Biography">
            <RichTextEditor value={form.bio} onChange={(html) => setForm(f => ({...f, bio: html}))} />
          </Field>

          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_visible} onChange={e => setForm({...form, is_visible: e.target.checked})} className="accent-[color:var(--blue)]" />
            Visible on the website
          </label>

          <div className="flex gap-3">
            <button type="submit" className="btn-primary" data-testid="staff-form-save"><Save className="w-4 h-4" /> Save</button>
            <button type="button" onClick={cancelEdit} className="btn-ghost">Cancel</button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-center text-[color:var(--muted)] py-8">Loading…</p>
      ) : staff.length === 0 ? (
        <p className="text-center text-[color:var(--muted)] py-8">No staff members yet.</p>
      ) : (
        <div className="space-y-8">
          {grouped.map(group => (
            <section key={group.value}>
              <h3 className="text-sm uppercase tracking-wider text-[color:var(--muted)] font-mono-accent mb-3">
                {group.label} <span className="text-[color:var(--blue-deep)]">· {group.items.length}</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {group.items.map(s => (
                  <div key={s.id} className={`flex gap-3 p-3 rounded-xl border ${s.is_visible ? "border-[color:var(--line)] bg-white" : "border-dashed border-[color:var(--line)] bg-[color:var(--cream)]/50 opacity-60"}`}
                       data-testid={`admin-staff-row-${s.id}`}>
                    {s.photo_url ? (
                      <img src={s.photo_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${s.photo_url}` : s.photo_url}
                           alt="" className="w-14 h-14 object-cover rounded-lg" />
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-[color:var(--cream)] flex items-center justify-center text-[color:var(--muted)] text-xs">
                        {s.name.split(" ").map(n => n[0]).slice(0, 2).join("")}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[color:var(--blue-ink)] truncate">{s.name}</div>
                      <div className="text-xs text-[color:var(--muted)] truncate">{s.role || "-"} · order {s.display_order}</div>
                    </div>
                    <div className="flex items-start gap-1">
                      <button onClick={() => toggleVisible(s)} title={s.is_visible ? "Visible" : "Hidden"} className="p-1.5 rounded hover:bg-[color:var(--cream)]" data-testid={`staff-toggle-${s.id}`}>
                        {s.is_visible ? <Eye className="w-4 h-4 text-[color:var(--blue-deep)]" /> : <EyeOff className="w-4 h-4 text-[color:var(--muted)]" />}
                      </button>
                      <button onClick={() => startEdit(s)} className="p-1.5 rounded hover:bg-[color:var(--cream)]" data-testid={`staff-edit-${s.id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => remove(s)} className="p-1.5 rounded text-red-600 hover:bg-red-50" data-testid={`staff-delete-${s.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(a) => setForm(f => ({...f, photo_url: a.public_url}))}
      />
    </div>
  );
}

const ic = "w-full rounded-lg border border-[color:var(--line)] px-3 py-2 bg-white focus:outline-none focus:border-[color:var(--blue)]";
function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-[color:var(--ink-soft)] mb-1">{label}</label>{children}</div>;
}
